import { gql, useApolloClient, useMutation } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import { Delta } from 'quill/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { IS_APPLE } from '../constants/os';
import type {
  Editor_PostFragment,
  PostMentionMutation,
  PostMentionMutationVariables,
  UpdatePostTitleFragment,
} from '../graphql/generated/graphqlTypes';
import showFullPageSheet from '../post/comment/showFullPageSheet';
import getThreadIdFromUrlHash from '../post/getThreadIdFromUrlHash';
import { getScrollContainer } from '../post/listenToPostScroll';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from '../post/postPolicyCheck';
import { UserRole } from '../topic/topicPolicyCheck';
import UIActions from '../ui/UIActions';
import Logger from '../utils/Logger';
import useNavigate from '../utils/router/useNavigate';
import useIsPublicView from '../utils/useIsPublicView';
import useShortcut from '../utils/useShortcut';
import type { ClipboardHooks } from './Clipboard';
import { ClipboardHooksContext } from './ClipboardHooksContext';
import styles from './Editor.module.scss';
import EditorPlaceholder from './EditorPlaceholder';
import Gutter from './Gutter';
import History from './History';
import LinkCursorSelection from './LinkCursorSelection';
import MultiCursor from './MultiCursor';
import QuillComponent from './QuillComponent';
import { useQuill } from './QuillContext';
import Quill from './QuillEditor';
import EmojiAutocompleter from './controls/EmojiAutocompleter';
import MentionContext from './controls/MentionContext';
import MentionPicker from './controls/MentionPicker';
import SlashCommandMenu from './controls/SlashCommandMenu';
import AttachmentToolbar from './controls/toolbar/AttachmentToolbar';
import EditorToolbar from './controls/toolbar/EditorToolbar';
import EmbedToolbar from './controls/toolbar/EmbedToolbar';
import ImageToolbar from './controls/toolbar/ImageToolbar';
import VideoToolbar from './controls/toolbar/VideoToolbar';
import { CREATION_ID, EditorActions } from './editorSlice';
import Annotation from './formats/Annotation';
import Comment from './formats/Comment';
import ImageBlot from './formats/ImageBlot';
import Reaction from './formats/Reaction';
import type { MagneticWallAttributor } from './formats/utils/attributors';
import {
  getAttributorfromMagneticWallType,
  getClosestAttribute,
} from './formats/utils/attributors';
import { areCarouselImages } from './image/Carousel';
import EditorModal from './modals/EditorModal';
import useSpellCorrector from './spellCorrector/useSpellCorrector';
import type { EmitterSource, Range } from './types';
import useAICompose from './useAICompose';
import useLinkShortcut from './useLinkShortcut';
import DraggingAutoScroll from './utils/DraggingAutoScroll';
import copyAsMarkdown from './utils/copyAsMarkdown';
import getHeaderCollapse from './utils/getHeaderCollapse';
import { doesBlotManageRemoteSelections } from './utils/getNestedTargetForRemoteSelection';
import onDnDDragEnd from './utils/onDnDDragEnd';
import removeMagneticWallAttributors from './utils/removeMagneticWallAttributors';

const EDITOR_MODULE_CONFIG = {
  prefixListener: true,
  headerId: true,
  headerCollapse: true,
  scrollToAnchor: true,
  hoveredBlotListener: true,
};

const POST_MENTION_MUTATION = gql`
  mutation postMention($postId: ID!, $userId: ID!) {
    postMention(id: $postId, userId: $userId) {
      id
    }
  }
`;

const POST_FRAGMENT = gql`
  fragment Editor_post on PublicPostFields {
    id
    ...AttachmentToolbar_post
    ...EditorPlaceholder_post
    ...isAllowedThroughLinkAccess_post
    ... on Post {
      externalPost {
        id
        ...EditorToolbar_externalPost
      }
    }
  }
  ${AttachmentToolbar.fragments.post}
  ${EditorPlaceholder.fragments.post}
  ${isAllowedThroughLinkAccess.fragments.post}
  ${EditorToolbar.fragments.externalPost}
`;

const useEditorShortcuts = (isMounted: boolean, isExternalPost: boolean) => {
  const {
    quill,
    selection: { range, formats },
  } = useQuill();
  const dispatch = useAppDispatch();
  const skip = !isMounted;

  const handleCommentShortcut = (e: KeyboardEvent) => {
    if (range?.length && !formats.comment && !isExternalPost) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      dispatch(EditorActions.focusComment({ id: CREATION_ID, isActive: true }));
    }
    e.preventDefault();
  };

  useShortcut(
    'mod+a',
    (e) => {
      quill?.setSelection(0, quill.getLength(), Quill.sources.USER);
      e.preventDefault();
    },
    { skip },
  );

  useShortcut(
    'mod+s',
    (e) => {
      e.preventDefault();
    },
    { global: true, skip },
  );

  useShortcut('mod+option+a', handleCommentShortcut, { global: true, skip });

  // On Mac avoid Chrome's minimize window shortcut
  useShortcut('mod+option+m', handleCommentShortcut, {
    global: true,
    skip: skip || !IS_APPLE,
  });

  // On Windows avoid NVidia GeForce Experience shortcut
  useShortcut('mod+option+m', handleCommentShortcut, {
    global: true,
    action: 'keyup',
    skip: skip || IS_APPLE,
  });

  useShortcut(
    ['e', 'i'],
    (e) => {
      dispatch(EditorActions.readOnly(false));
      e.preventDefault();
    },
    { skip: isExternalPost },
  );

  useShortcut(
    'mod+enter',
    (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT') {
        dispatch(EditorActions.readOnly(true));
        e.preventDefault();
      }
    },
    { global: true, skip: isExternalPost },
  );

  useShortcut(
    'mod+shift+c',
    async (e) => {
      const r = quill?.getSelection();
      if (r?.length && quill) {
        e.preventDefault();
        await copyAsMarkdown(quill, r);
      }
    },
    { global: true },
  );
};

const useShouldAutoFocus = () => {
  const shouldAutoFocusRef = useRef<boolean | null>(null);

  if (typeof shouldAutoFocusRef.current === 'boolean') {
    return shouldAutoFocusRef.current;
  }

  const shouldAutoFocus = !getThreadIdFromUrlHash(document.location.hash);
  shouldAutoFocusRef.current = shouldAutoFocus;
  return shouldAutoFocus;
};

const isPostContentEmpty = (initialContent: Delta | null) => {
  const { ops } = new Delta(initialContent || []);
  return (
    ops.length === 1 &&
    _.isEmpty(_.omit(ops[0]?.attributes, ['author'])) &&
    (ops[0]?.insert === '\n' || ops[0]?.insert === '\n\n')
  );
};

const useBlockPlaceholder = (enabled: boolean) => {
  const { quill } = useQuill();

  useEffect(() => {
    if (!quill || !enabled) return undefined;

    const manager = {
      hasPlaceholder: false, // local flag to improve performance
      getAll() {
        return this.hasPlaceholder
          ? Array.from(
              quill.root.querySelectorAll(`[${styles.placeholderAttribute}]`),
            )
          : [];
      },
      removeAll() {
        this.getAll().forEach((node) =>
          node.removeAttribute(styles.placeholderAttribute),
        );
        this.hasPlaceholder = false;
      },
      set(node: Element) {
        this.removeAll();
        node.setAttribute(styles.placeholderAttribute, '');
        this.hasPlaceholder = true;
      },
    };

    const listener = (
      type: string,
      _old: unknown,
      _newDelta: unknown,
      source: EmitterSource,
    ) => {
      if (type === Quill.events.SELECTION_CHANGE) {
        const selectedNode = quill.selection.lastNative?.start.node;
        if (
          selectedNode instanceof Element &&
          selectedNode.tagName === 'BR' &&
          selectedNode.parentNode instanceof Element &&
          selectedNode.parentNode.tagName === 'P'
        ) {
          manager.set(selectedNode.parentNode);
        } else {
          manager.removeAll();
        }
      } else if (type === Quill.events.TEXT_CHANGE) {
        if (source === Quill.sources.USER) {
          manager.removeAll();
        } else {
          const nodes = manager.getAll();
          const node = nodes[0];
          if (node) {
            const blot = quill.scroll.find(node);
            const isNodeEmpty = blot ? blot.length() <= 1 : false;
            if (isNodeEmpty) {
              nodes
                .slice(1)
                .forEach((n) => n.removeAttribute(styles.placeholderAttribute));
            } else {
              manager.removeAll();
            }
          }
        }
      }
    };

    quill.on(Quill.events.EDITOR_CHANGE, listener);
    return () => {
      quill.off(Quill.events.EDITOR_CHANGE, listener);
      manager.removeAll();
    };
  }, [quill, enabled]);
};

const useIsShowingPlaceholder = (allowPlaceholder: boolean) => {
  const { quill } = useQuill();
  const [hasContentChanged, setHasContentChanged] = useState(false);

  useEffect(() => {
    if (!quill || !allowPlaceholder || hasContentChanged) return undefined;

    const handler = () => {
      setHasContentChanged(true);
    };

    quill.once(Quill.events.TEXT_CHANGE, handler);
    return () => {
      quill.off(Quill.events.TEXT_CHANGE, handler);
    };
  }, [quill, hasContentChanged, allowPlaceholder]);

  return allowPlaceholder && !hasContentChanged;
};

function removeStatuses(quill: Quill, statuses: string[]) {
  const selector = statuses.map((status) => `[class*="-${status}"]`).join(', ');

  quill.root.querySelectorAll(selector).forEach((el) => {
    const classNamesWithPrefix = Array.from(el.classList).filter((className) =>
      statuses.some((status) => className.endsWith(`-${status}`)),
    );
    el.classList.remove(...classNamesWithPrefix);
  });
}

function addClassNameBySelector(
  quill: Quill,
  selector: string,
  className: string,
) {
  quill.root.querySelectorAll(selector).forEach((el) => {
    el.classList.add(className);
  });
}

function addStatusByAttributor(
  quill: Quill,
  attributor: MagneticWallAttributor,
  id: string,
  status: string,
) {
  addClassNameBySelector(
    quill,
    `[class*="${attributor.keyName}-${id}"]`,
    `status-${attributor.keyName}-${status}`,
  );
}

const useSyncMagneticWallStates = (quill: Quill | null) => {
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const magneticWallHighlight = useAppSelector(
    (state) => state.editor.magneticWallHighlight,
  );

  const expandCollapsedHeadersIfNecessary = useCallback(() => {
    if (!quill) return;
    const activeItem = quill.root.querySelector(`[class*="-active"]`);

    const blot = activeItem && quill.scroll.find(activeItem, true);
    if (blot) {
      getHeaderCollapse(quill)?.revealBlot(blot);
    }
  }, [quill]);

  useEffect(() => {
    if (!quill) return;

    removeStatuses(quill, ['focused', 'active', 'highlight']);

    if (magneticWallFocus) {
      const status = magneticWallFocus.active ? 'active' : 'focused';
      const attributor = getAttributorfromMagneticWallType(
        magneticWallFocus.type,
      );
      addStatusByAttributor(quill, attributor, magneticWallFocus.id, status);
    }

    if (magneticWallFocus?.active) {
      expandCollapsedHeadersIfNecessary();
    }
  }, [quill, magneticWallFocus, expandCollapsedHeadersIfNecessary]);

  useEffect(() => {
    if (!quill) return;

    removeStatuses(quill, ['highlight']);

    if (magneticWallHighlight) {
      const attributor = getAttributorfromMagneticWallType(
        magneticWallHighlight.type,
      );

      addStatusByAttributor(
        quill,
        attributor,
        magneticWallHighlight.id,
        'highlight',
      );
    }
  }, [quill, magneticWallHighlight]);
};

const useDnDAutoScroll = () => {
  const { readOnly } = useQuill();

  useEffect(() => {
    if (readOnly) return undefined;

    const scrollingContainer = getScrollContainer();
    if (!scrollingContainer) return undefined;

    const draggingAutoScroll = new DraggingAutoScroll(scrollingContainer);
    const off = onDnDDragEnd(document.body, () => {
      draggingAutoScroll.stop();
    });

    const dragEnterListener = () => {
      draggingAutoScroll.start();
    };
    const dragOverListener = (e: MouseEvent) => {
      draggingAutoScroll.setMouseYOffset(e.clientY);
    };
    scrollingContainer.addEventListener('dragenter', dragEnterListener);
    scrollingContainer.addEventListener('dragover', dragOverListener);

    return () => {
      off();
      draggingAutoScroll.stop();
      scrollingContainer.removeEventListener('dragenter', dragEnterListener);
      scrollingContainer.removeEventListener('dragover', dragOverListener);
    };
  }, [readOnly]);
};

export type TextChangeHandler = (
  delta: Delta,
  oldDelta: Delta,
  source: string,
) => void;

export type PerformanceMeasureHandler = (durations: {
  applied: number;
  mounted: number;
}) => void;

type EditorNamespace = 'editor' | 'publicEditor';

interface EditorContentProps {
  allowedEmbedHosts: string[];
  quill: Quill | null;
  hasSelection: boolean;
  readOnly: boolean;
  post: Editor_PostFragment;
  namespace?: EditorNamespace;
  newPost: boolean;
  onTextChange: TextChangeHandler;
  onPerformanceMeasure?: PerformanceMeasureHandler;
}

function useEditorClipboardHooks() {
  return useMemo((): ClipboardHooks => {
    let lastCutHtml: string | null = null;

    return {
      beforeCut: (html) => {
        lastCutHtml = html;
        return html;
      },
      beforePaste: (html) => {
        const hasBeenCutLocally =
          html.replace("<meta charset='utf-8'>", '') === lastCutHtml;

        // pasting more than once should be treated like a copy,
        // therefore we reset the lastCutHTML state
        lastCutHtml = null;
        if (hasBeenCutLocally) return html;
        return removeMagneticWallAttributors(html);
      },
    };
  }, []);
}

const EditorContent = React.memo(
  ({
    allowedEmbedHosts,
    hasSelection,
    quill,
    readOnly,
    post,
    namespace = 'editor',
    newPost,
    onTextChange,
    onPerformanceMeasure,
  }: EditorContentProps) => {
    const initialContent = useAppSelector(
      (state) => state.editor.initialContent,
    );
    const navigate = useNavigate();
    const title = useAppSelector((state) => state.editor.title);
    const shouldAutoFocus = useShouldAutoFocus();
    const isPublicView = useIsPublicView();

    const [isQuillMounted, setIsQuillMounted] = useState(false);

    useEditorShortcuts(
      isQuillMounted,
      'externalPost' in post && !!post.externalPost,
    );
    const dispatch = useAppDispatch();
    const client = useApolloClient();
    const isPlaceholderAllowed =
      !readOnly && newPost && isPostContentEmpty(initialContent);
    const isShowingEditorPlaceholder =
      useIsShowingPlaceholder(isPlaceholderAllowed);

    useBlockPlaceholder(!isShowingEditorPlaceholder);

    const handleMount = () => {
      setIsQuillMounted(true);
    };

    const handleSelectionChange = (
      newRange: Range | null,
      yChanged?: boolean,
    ) => {
      if (newRange == null) {
        if (hasSelection) {
          dispatch(EditorActions.transmitStateChange(null));
        }
        return;
      }

      if (!yChanged) return;

      if (newRange.length <= 1) {
        const [leaf] = quill?.getLeaf(newRange.index) || [];
        if (leaf && doesBlotManageRemoteSelections(leaf)) {
          return;
        }
      }

      dispatch(
        EditorActions.transmitStateChange({ type: 'plain', ...newRange }),
      );
    };

    const handleTextChange = (
      delta: Delta,
      oldContent: Delta,
      source: string,
    ) => {
      if (source === Quill.sources.USER) {
        dispatch(EditorActions.localUpdated(delta));
        const newTitle = quill?.getTitle();
        if (
          newTitle !== undefined &&
          newTitle !== title &&
          !(newTitle === '' && title === 'Untitled')
        ) {
          dispatch(EditorActions.titleUpdate(newTitle, navigate, isPublicView));

          client.writeFragment<UpdatePostTitleFragment>({
            id: `Post:${post.id}`,
            fragment: gql`
              fragment updatePostTitle on Post {
                id
                title
              }
            `,
            fragmentName: 'updatePostTitle',
            data: { id: post.id, title: newTitle },
          });
        }
      } else if (source !== Quill.sources.API) {
        Logger.message(`Unknown source in onTextChange ${source}`);
      }
      if (onTextChange) onTextChange(delta, oldContent, source);
    };

    const [mentionUserMutate] = useMutation<
      PostMentionMutation,
      PostMentionMutationVariables
    >(POST_MENTION_MUTATION);
    const mentionUser = useCallback(
      (userId: string) => {
        mentionUserMutate({ variables: { postId: post.id, userId } });
      },
      [mentionUserMutate, post.id],
    );

    const mentionContextValue = useMemo(
      () => ({ postId: post.id, onMentionUser: mentionUser }),
      [post.id, mentionUser],
    );

    const clipboardHooks = useEditorClipboardHooks();

    return (
      <ClipboardHooksContext.Provider value={clipboardHooks}>
        <MentionContext.Provider value={mentionContextValue}>
          {quill && (
            <>
              <EditorToolbar
                showInTitleLine
                readOnly={readOnly}
                externalPost={'externalPost' in post ? post.externalPost : null}
              />
              <MentionPicker quill={quill} readOnly={readOnly} />
              <EmojiAutocompleter quill={quill} readOnly={readOnly} />
              <ImageToolbar />
              <EmbedToolbar />
              <VideoToolbar />
              <AttachmentToolbar post={post} />
            </>
          )}
          <QuillComponent
            preloadNearScrollingContainerBlots
            autoFocus={shouldAutoFocus}
            namespace={namespace}
            rootClassName={classNames(styles.editor, 'post-editor')}
            content={initialContent || undefined}
            contentPlaceholder={
              isShowingEditorPlaceholder || readOnly
                ? ''
                : 'Put your ideas to paper...'
            }
            readOnly={readOnly}
            moduleConfig={{
              ...EDITOR_MODULE_CONFIG,
              clipboard: { allowedEmbedHosts, clipboardHooks },
            }}
            onSelectionChange={handleSelectionChange}
            onTextChange={handleTextChange}
            onMount={handleMount}
            onPerformanceMeasure={onPerformanceMeasure}
          />
          {quill && (
            <>
              {isShowingEditorPlaceholder && <EditorPlaceholder post={post} />}
              <MultiCursor quill={quill} />
              <LinkCursorSelection />
              <SlashCommandMenu
                quill={quill}
                readOnly={readOnly}
                allowedEmbedHosts={allowedEmbedHosts}
              />
              <Gutter quill={quill} readOnly={readOnly} />
            </>
          )}
        </MentionContext.Provider>
      </ClipboardHooksContext.Provider>
    );
  },
);

EditorContent.displayName = 'EditorContent';

interface EditorProps {
  allowedEmbedHosts: string[];
  post: Editor_PostFragment;
  namespace?: EditorNamespace;
  highlightedRangeIndex?: number;
  onTextChange: TextChangeHandler;
  onPerformanceMeasure?: PerformanceMeasureHandler;
  hasBanner?: boolean;
}

const Editor = ({
  allowedEmbedHosts,
  post,
  namespace,
  highlightedRangeIndex,
  onTextChange,
  onPerformanceMeasure,
  hasBanner,
}: EditorProps) => {
  const { quill, selection } = useQuill();
  useAICompose(post.id);
  useSpellCorrector(post.id);
  const dispatch = useAppDispatch();
  useLinkShortcut();
  useSyncMagneticWallStates(quill);
  useDnDAutoScroll();

  const isPublicView = useIsPublicView();

  const version = useAppSelector((state) => state.editor.version);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  useEffect(() => {
    if (!quill || !highlightedRangeIndex) return;

    const [leaf] = quill.getLeaf(highlightedRangeIndex);
    const node = leaf?.domNode;
    if (node instanceof Element) {
      node.scrollIntoView();
    } else if (node instanceof Node) {
      node.parentElement?.scrollIntoView();
    }
  }, [highlightedRangeIndex, quill]);

  useEffect(() => {
    if (quill?.history instanceof History) {
      quill.history.postIdForTrackingUndo = post.id;
    }
  }, [quill, post.id]);

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    if (
      !window?.getSelection?.()?.toString().length ||
      Quill.find(target, true) instanceof ImageBlot
    ) {
      const attribute = getClosestAttribute(target);

      if (attribute) {
        const shouldShowPopOver = readOnly || !showFullPageSheet();

        if (attribute.attributor === Comment) {
          dispatch(
            EditorActions.focusComment({
              id: attribute.id,
              isActive: shouldShowPopOver,
            }),
          );
        } else if (attribute.attributor === Reaction) {
          dispatch(
            EditorActions.focusReaction({
              id: attribute.id,
              isActive: shouldShowPopOver,
            }),
          );
        } else if (attribute.attributor === Annotation) {
          dispatch(
            EditorActions.focusAnnotation({
              id: attribute.id,
              isActive: shouldShowPopOver,
            }),
          );
        }
      }
    }
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { target } = e;

    if (ImageBlot.isImageOrImageContainerNode(target)) {
      const blot = Quill.find(target, true);
      if (blot instanceof ImageBlot) {
        const { value, carouselIndex } = blot.store.getState();
        if (Array.isArray(value)) {
          if (areCarouselImages(value)) {
            dispatch(
              UIActions.openModal(EditorModal, {
                carousel: {
                  images: value,
                  index: carouselIndex ?? 0,
                },
              }),
            );
          } else {
            const firstImage = value[0];
            if (!firstImage) return;
            dispatch(
              UIActions.openModal(EditorModal, {
                image: {
                  src: firstImage.source,
                  caption: firstImage.caption,
                },
              }),
            );
          }
        }
      }
    }
  };

  const handleMouseOver = (e: React.MouseEvent) => {
    if (!quill) return;

    const { target } = e;
    if (target instanceof HTMLElement) {
      removeStatuses(quill, ['highlight']);

      const attribute = getClosestAttribute(target);
      if (attribute) {
        addStatusByAttributor(
          quill,
          attribute.attributor,
          attribute.id,
          'highlight',
        );
      }
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
    <div
      className={classNames('editor-container', {
        [styles.public]:
          !isPublicView &&
          isAllowedThroughLinkAccess(
            PostPolicyAction.ViewPost,
            post,
            UserRole.Anonymous,
          ),
        [styles.hasBanner]: hasBanner,
      })}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseOver={handleMouseOver}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      data-version={version}
    >
      <EditorContent
        allowedEmbedHosts={allowedEmbedHosts}
        post={post}
        namespace={namespace}
        quill={quill}
        readOnly={readOnly}
        hasSelection={!!selection.range}
        newPost={version === 0}
        onTextChange={onTextChange}
        onPerformanceMeasure={onPerformanceMeasure}
      />
    </div>
  );
};

Editor.fragments = { post: POST_FRAGMENT };

export default Editor;
